import React, { useMemo, useState } from 'react';
import {
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type { ComparisonCveRow, ComparisonStatus } from './ReleaseComparison';
import { SEVERITY_CHOICES } from '../consts';

const STATUS_COLORS: Record<ComparisonStatus, string> = {
  fixed: '#2E7D32',
  unfixed: '#D32F2F',
  new: '#ED6C02',
};

const STATUS_LABELS: Record<ComparisonStatus, string> = {
  fixed: 'Fixed',
  unfixed: 'Unfixed',
  new: 'New',
};

const STATUS_RANK: Record<ComparisonStatus, number> = {
  fixed: 0,
  unfixed: 1,
  new: 2,
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#D32F2F',
  high: '#F57C00',
  medium: '#FBC02D',
  low: '#FFF176',
  none: '#BDBDBD',
};

const SEVERITY_RANK: Record<string, number> = SEVERITY_CHOICES.reduce(
  (acc, choice, idx) => ({ ...acc, [choice.id]: idx }),
  {} as Record<string, number>
);

const SEVERITY_LABELS: Record<string, string> = SEVERITY_CHOICES.reduce(
  (acc, choice) => ({ ...acc, [choice.id]: choice.name }),
  {} as Record<string, string>
);

type SortField =
  | 'comparisonStatus'
  | 'name'
  | 'severity'
  | 'cvssScore'
  | 'imageRepository'
  | 'imageTag'
  | 'availableFixedImageBuildTag'
  | 'availableFixedReleaseVersion';

type GroupField = 'none' | 'comparisonStatus' | 'severity';

const COLUMNS: Array<{ field: SortField; label: string }> = [
  { field: 'comparisonStatus', label: 'Status' },
  { field: 'name', label: 'CVE' },
  { field: 'severity', label: 'Severity' },
  { field: 'cvssScore', label: 'CVSS Score' },
  { field: 'imageRepository', label: 'Image' },
  { field: 'imageTag', label: 'Tag' },
  { field: 'availableFixedImageBuildTag', label: 'Available Fixed Image Tag' },
  { field: 'availableFixedReleaseVersion', label: 'Available Fixed Release Version' },
];

const getSortValue = (row: ComparisonCveRow, field: SortField): string | number => {
  switch (field) {
    case 'comparisonStatus':
      return STATUS_RANK[row.comparisonStatus];
    case 'severity':
      return SEVERITY_RANK[row.severity] ?? SEVERITY_CHOICES.length;
    case 'cvssScore':
      return row.cvssScore ?? -1;
    default:
      return (row[field] ?? '').toString().toLowerCase();
  }
};

const DISPLAY_CHUNK = 20;

const CveRows = ({ rows }: { rows: ComparisonCveRow[] }) => {
  const [expanded, setExpanded] = useState(false);
  const displayCount = expanded ? rows.length : Math.min(DISPLAY_CHUNK, rows.length);
  const hasMore = rows.length > DISPLAY_CHUNK;

  return (
    <>
      {rows.slice(0, displayCount).map((row, idx) => (
        <TableRow key={`${row.comparisonStatus}-${row.name}-${idx}`}>
          <TableCell>
            <Chip
              label={STATUS_LABELS[row.comparisonStatus]}
              size="small"
              sx={{ backgroundColor: STATUS_COLORS[row.comparisonStatus], color: 'white', fontWeight: 'bold' }}
            />
          </TableCell>
          <TableCell>{row.name}</TableCell>
          <TableCell>
            <Chip
              label={(SEVERITY_LABELS[row.severity] ?? row.severity).toUpperCase()}
              size="small"
              sx={{ backgroundColor: SEVERITY_COLORS[row.severity] ?? '#BDBDBD', fontWeight: 'bold' }}
            />
          </TableCell>
          <TableCell>{row.cvssScore ?? '-'}</TableCell>
          <TableCell sx={{ fontSize: '0.85rem' }}>{row.imageRepository}</TableCell>
          <TableCell>{row.imageTag}</TableCell>
          <TableCell>{row.availableFixedImageBuildTag ?? '-'}</TableCell>
          <TableCell>{row.availableFixedReleaseVersion ?? '-'}</TableCell>
        </TableRow>
      ))}
      {hasMore && (
        <TableRow>
          <TableCell colSpan={COLUMNS.length} sx={{ textAlign: 'center', borderBottom: 'none' }}>
            <Button size="small" variant="outlined" onClick={() => setExpanded(!expanded)}>
              {expanded ? 'Show Less' : `Show All ${rows.length} CVEs`}
            </Button>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

/**
 * Single table combining the fixed/unfixed/new CVE buckets, with sortable
 * columns (click a header) and an optional group-by for Status or Severity.
 */
const ComparisonCveTable = ({ rows }: { rows: ComparisonCveRow[] }) => {
  const [sortField, setSortField] = useState<SortField>('comparisonStatus');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [groupBy, setGroupBy] = useState<GroupField>('none');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = getSortValue(a, sortField);
      const bv = getSortValue(b, sortField);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [rows, sortField, sortDirection]);

  const groups = useMemo(() => {
    if (groupBy === 'none') {
      return [{ key: 'all', label: null as string | null, rows: sortedRows }];
    }
    const rank: Record<string, number> =
      groupBy === 'comparisonStatus' ? STATUS_RANK : SEVERITY_RANK;
    const labels: Record<string, string> =
      groupBy === 'comparisonStatus' ? STATUS_LABELS : SEVERITY_LABELS;
    const byKey = new Map<string, ComparisonCveRow[]>();
    sortedRows.forEach((row) => {
      const key = String(row[groupBy]);
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key)!.push(row);
    });
    return Array.from(byKey.entries())
      .sort(([a], [b]) => (rank[a] ?? 99) - (rank[b] ?? 99))
      .map(([key, groupRows]) => ({
        key,
        label: (labels[key] ?? key).toString(),
        rows: groupRows,
      }));
  }, [sortedRows, groupBy]);

  return (
    <Paper sx={{ p: 2 }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ mb: 2, flexWrap: 'wrap' }}
      >
        <Typography variant="subtitle2">Group by:</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={groupBy}
          onChange={(_, value) => value && setGroupBy(value)}
        >
          <ToggleButton value="none">None</ToggleButton>
          <ToggleButton value="comparisonStatus">Status</ToggleButton>
          <ToggleButton value="severity">Severity</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell key={col.field}>
                  <TableSortLabel
                    active={sortField === col.field}
                    direction={sortField === col.field ? sortDirection : 'asc'}
                    onClick={() => handleSort(col.field)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length}>
                  <Typography sx={{ p: 2 }}>No CVEs to display</Typography>
                </TableCell>
              </TableRow>
            )}
            {groups.map((group) => (
              <React.Fragment key={group.key}>
                {group.label && (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMNS.length}
                      sx={{ fontWeight: 'bold', backgroundColor: 'action.hover' }}
                    >
                      {group.label} ({group.rows.length})
                    </TableCell>
                  </TableRow>
                )}
                <CveRows rows={group.rows} />
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ComparisonCveTable;
